import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { PanelMenuModule } from 'primeng/panelmenu';
import { AvatarModule } from 'primeng/avatar';
import { Subject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { ProjectStateService } from '../../services/project-state.service';
import { ProjectModel } from '../../models/project.model';
import { SSOService } from '../../services/sso.service';
import { UserService } from '../../services/user.service';
import { ProjectService } from '../../services/project.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS } from '../../constants/permission.constants';
import { PROJECT_MENU_PERMISSION_KEYS } from '../../constants/project-menu.constants';
import { TopMenuComponent } from '../../shared/components/top-menu/top-menu';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { EffectiveSkidConfigurationService } from '../../services/effective-skid-configuration.service';
import { getProjectSkidConfigurationParts, SkidConfigurationParts } from '../../utils/skid-configuration.utils';
import { ChatBubbleComponent } from '../../shared/components/chat-bubble/chat-bubble';

@Component({
    selector: 'app-project-layout',
    imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, TopMenuComponent, ChatBubbleComponent],
    templateUrl: './project-layout.html',
    styleUrl: './project-layout.css',
})
export class ProjectLayout implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    projectId: number = 0;
    projectMenuItems: any[] = [];
    projectInfo: ProjectModel | null = null;
    effectiveSkidParts: SkidConfigurationParts = [0, 0, 0, 0, 0, 0, 0];
    projectName: string = 'Project';
    currentPageName: string = '';
    private routeSubscription: Subscription = new Subscription();
    menuReady = signal(false);

    get sidebarCollapsed(): boolean {
        return this.sidebarState.collapsed();
    }

    toggleSidebar(): void {
        this.sidebarState.toggle();
    }

    constructor(
        private projectStateService: ProjectStateService,
        private ssoService: SSOService,
        private userService: UserService,
        private projectService: ProjectService,
        private permissionHelper: PermissionHelperService,
        private sidebarState: SidebarStateService,
        private effectiveSkidConfiguration: EffectiveSkidConfigurationService
    ) {}

    ngOnInit(): void {
        this.projectStateService.currentProject$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(project => {
            if (project && project.projectId) {
                this.projectId = project.projectId;
                this.projectInfo = project;
                this.effectiveSkidParts = getProjectSkidConfigurationParts(project);
                this.projectName = project.projectName;
                this.loadEffectiveSkidConfiguration(project);
                this.tryBuildProjectMenu();
            }
        });

        this.projectStateService.currentProjectPermissions$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.tryBuildProjectMenu();
        });

        combineLatest([this.projectStateService.currentProjectId$, this.ssoService.user$]).pipe(
            takeUntil(this.destroy$),
            filter(([projectId, user]) => !!projectId && !!user?.userId),
            distinctUntilChanged(([prevProjectId, prevUser], [nextProjectId, nextUser]) => {
                return prevProjectId === nextProjectId && prevUser?.userId === nextUser?.userId;
            }),
        ).subscribe(([projectId, user]) => {
            if (projectId && user?.userId) {
                this.refreshCurrentProjectUserActive(projectId, user.userId);
            } else {
                this.projectStateService.setCurrentProjectUserActive(null);
            }
        });

        this.projectStateService.currentProjectId$.pipe(
            takeUntil(this.destroy$),
            filter((projectId): projectId is number => !!projectId),
            distinctUntilChanged(),
        ).subscribe(projectId => {
            this.refreshCurrentProjectPermissions(projectId);
        });

        this.projectStateService.projectUsersRefresh$.pipe(
            takeUntil(this.destroy$),
            filter(projectId => !!projectId && projectId === this.projectId)
        ).subscribe({
            next: (projectId) => {
                const user = this.ssoService.getCurrentUser();
                if (user?.userId) {
                    this.refreshCurrentProjectUserActive(projectId, user.userId);
                }
                this.refreshCurrentProjectPermissions(projectId);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    private tryBuildProjectMenu(): void {
        if (this.projectId <= 0) {
            this.projectMenuItems = [];
            this.menuReady.set(false);
            return;
        }

        const permissions = this.projectStateService.getCurrentProjectPermissions();
        if (!permissions) {
            this.projectMenuItems = [];
            this.menuReady.set(false);
            return;
        }

        const canView = (pageKey: string) => this.permissionHelper.hasAnyForPage(pageKey);
        const canShowBlockSchedule = this.hasSkidConfiguration();
        const canShowMenu = (pageKey: string, menuPermissionKey: string) =>
            canView(pageKey) && this.projectStateService.hasPermission('PROJECT_MENU', menuPermissionKey);

        this.projectMenuItems = [
            ...(canShowMenu(PAGE_KEYS.DASHBOARD, PROJECT_MENU_PERMISSION_KEYS.DASHBOARD) ? [{
                label: 'Dashboard',
                icon: 'ri-dashboard-line',
                routerLink: ['/dashboard', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.EQUIPMENT, PROJECT_MENU_PERMISSION_KEYS.EQUIPMENT) ? [{
                label: 'Equipment',
                icon: 'ri-tools-line',
                routerLink: ['/equipment', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.TASKS, PROJECT_MENU_PERMISSION_KEYS.TASKS) ? [{
                label: 'Tasks',
                icon: 'ri-list-check',
                routerLink: ['/tasks', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.PLANS, PROJECT_MENU_PERMISSION_KEYS.PLANS) ? [{
                label: 'Plans',
                icon: 'ri-map-2-line',
                routerLink: ['/plans', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.PHOTOS, PROJECT_MENU_PERMISSION_KEYS.PHOTOS) ? [{
                label: 'Photos',
                icon: 'ri-image-line',
                routerLink: ['/photos', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.FORMS, PROJECT_MENU_PERMISSION_KEYS.FORMS) ? [{
                label: 'Forms',
                icon: 'ri-folder-open-line',
                routerLink: ['/forms', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.FILES, PROJECT_MENU_PERMISSION_KEYS.FILES) ? [{
                label: 'Files',
                icon: 'ri-file-line',
                routerLink: ['/files', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.BLOCK_SCHEDULE, PROJECT_MENU_PERMISSION_KEYS.BLOCK_SCHEDULE) && canShowBlockSchedule ? [{
                label: 'Block Schedule',
                icon: 'ri-briefcase-3-line',
                routerLink: ['/blockschedule', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.PROJECT_USERS, PROJECT_MENU_PERMISSION_KEYS.PROJECT_USERS) ? [{
                label: 'Users',
                icon: 'ri-team-line',
                routerLink: ['/projectusers', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.TEMPLATES, PROJECT_MENU_PERMISSION_KEYS.TEMPLATES) ? [{
                label: 'Template',
                icon: 'ri-stack-line',
                routerLink: ['/projecttemplates', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.TEMPLATES, PROJECT_MENU_PERMISSION_KEYS.INTEGRATIONS) ? [{
                label: 'Integrations',
                icon: 'ri-links-line',
                routerLink: ['/project-integrations', this.projectId]
            }] : []),
            ...(canShowMenu(PAGE_KEYS.SETTINGS, PROJECT_MENU_PERMISSION_KEYS.SETTINGS) ? [{
                label: 'Settings',
                icon: 'ri-settings-line',
                routerLink: ['/projectsettings', this.projectId]
            }] : [])
        ];

        this.menuReady.set(true);
    }

    private hasSkidConfiguration(): boolean {
        if (!this.projectInfo) return false;

        const totalFromParts = this.effectiveSkidParts.reduce((sum, value) => sum + value, 0);
        const totalSkids = Number.isFinite(Number(this.projectInfo.skids)) ? Number(this.projectInfo.skids) : 0;

        return totalFromParts > 0 || totalSkids > 0;
    }

    private loadEffectiveSkidConfiguration(project: ProjectModel): void {
        if (project.projectId) {
            this.effectiveSkidConfiguration.clear(project.projectId);
        }

        this.effectiveSkidConfiguration.getEffectiveParts(project)
            .pipe(takeUntil(this.destroy$))
            .subscribe(parts => {
                this.effectiveSkidParts = parts;
                this.tryBuildProjectMenu();
            });
    }

    private refreshCurrentProjectUserActive(projectId: number, userId: number): void {
        this.userService.getProjectUserList(projectId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (users) => {
                    const matchedUser = users?.find(u => u.userId === userId);
                    const isActive = matchedUser?.isActive;

                    if (typeof isActive === 'boolean') {
                        this.projectStateService.setCurrentProjectUserActive(isActive);
                    } else {
                        this.projectStateService.setCurrentProjectUserActive(true);
                    }
                },
                error: () => {
                    this.projectStateService.setCurrentProjectUserActive(null);
                }
            });
    }

    private refreshCurrentProjectPermissions(projectId: number): void {
        this.projectService.getUserProjectPermissions(projectId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (permissions) => {
                    this.projectStateService.setCurrentProjectPermissions(permissions);
                },
                error: () => {
                    this.projectStateService.setCurrentProjectPermissions(null);
                }
            });
    }

    async onLogout(): Promise<void> {
        try {
            await this.ssoService.logout();
        } catch (error) {
            console.error('Logout failed:', error);
            localStorage.clear();
            location.assign('/login');
        }
    }
}
